import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { Clear as ClearIcon, Create as CreateIcon } from '@mui/icons-material';
import { isInkFieldType, isMergeField, mergeKeyMeta, fieldDisplayLabel } from '../../utils/esignFields';

function makeTypedInk(text, { width = 400, height = 100, fontSize = 48 } = {}) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.font = `${fontSize}px "Segoe Script", "Brush Script MT", cursive`;
  ctx.fillStyle = '#000000';
  ctx.fillText(text, 16, Math.round(height * 0.65));
  return canvas.toDataURL('image/png');
}

/**
 * Collect values for BYO esign fields assigned to the current signer.
 * Merge fields are system-filled — shown read-only or omitted from ink UI.
 */
const SignerFieldsForm = ({
  fields = [],
  values,
  onChange,
  disabled,
  mergePreview = {},
  /** Hide types completed on the PDF preview (e.g. checkboxes). */
  hideTypes = []
}) => {
  const [drawFieldId, setDrawFieldId] = useState(null);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const hideSet = new Set(hideTypes || []);
  const collectable = fields.filter((f) => !isMergeField(f) && !hideSet.has(f.type));
  const mergeFields = fields.filter((f) => isMergeField(f));
  const hiddenCheckboxes = fields.filter(
    (f) => !isMergeField(f) && f.type === 'checkbox' && hideSet.has('checkbox')
  );

  useEffect(() => {
    const ink = collectable.find((f) => isInkFieldType(f.type) && f.required !== false);
    if (ink && !drawFieldId) setDrawFieldId(ink.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !drawFieldId) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [drawFieldId]);

  if (!collectable.length && !mergeFields.length && !hiddenCheckboxes.length) return null;

  const setValue = (id, value) => {
    onChange({ ...(values || {}), [id]: value });
  };

  const pointerToClient = (e) => {
    if (e.touches?.[0]) {
      return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
    }
    return { clientX: e.clientX, clientY: e.clientY };
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    ctx.beginPath();
    ctx.moveTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    ctx.lineTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas && drawFieldId) {
      setValue(drawFieldId, canvas.toDataURL('image/png'));
    }
  };

  const clearInk = (fieldId) => {
    if (drawFieldId === fieldId && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    setValue(fieldId, '');
  };

  return (
    <Box sx={{ mb: 2 }}>
      {hiddenCheckboxes.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Tap each checkbox box on the <strong>document preview</strong> to read and confirm those
          acknowledgments
          {hiddenCheckboxes.some((f) => {
            const v = values?.[f.id];
            const checked =
              v === true || v === 'true' || v === 1 || v === '1' || v === 'yes';
            return f.required !== false && !checked;
          })
            ? ` (${hiddenCheckboxes.filter((f) => {
                const v = values?.[f.id];
                const checked =
                  v === true || v === 'true' || v === 1 || v === '1' || v === 'yes';
                return f.required !== false && !checked;
              }).length} still needed)`
            : ' (all confirmed)'}
          .
        </Alert>
      )}
      {collectable.length > 0 && (
        <>
          <Typography variant="subtitle1" gutterBottom>
            Required fields
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Complete each field placed for you on this document.
          </Alert>
        </>
      )}
      {collectable.map((field) => {
        const checkboxPeers =
          field.type === 'checkbox'
            ? collectable.filter((f) => f.type === 'checkbox')
            : [];
        const label = fieldDisplayLabel(field, {
          indexAmongType:
            field.type === 'checkbox' ? checkboxPeers.findIndex((f) => f.id === field.id) : undefined
        });
        if (isInkFieldType(field.type)) {
          const isActive = drawFieldId === field.id;
          return (
            <Box key={field.id} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                {label}
                {field.required !== false ? ' *' : ''}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Button
                  size="small"
                  variant={isActive ? 'contained' : 'outlined'}
                  startIcon={<CreateIcon />}
                  disabled={disabled}
                  onClick={() => setDrawFieldId(field.id)}
                >
                  Draw
                </Button>
                <TextField
                  size="small"
                  placeholder="Or type initials / name"
                  disabled={disabled}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v.trim()) {
                      setValue(
                        field.id,
                        makeTypedInk(v, {
                          width: field.type === 'initials' ? 200 : 400,
                          height: field.type === 'initials' ? 80 : 100,
                          fontSize: field.type === 'initials' ? 40 : 48
                        })
                      );
                    } else {
                      setValue(field.id, '');
                    }
                  }}
                />
              </Box>
              {isActive && (
                <Paper
                  sx={{
                    border: '2px solid #ddd',
                    borderRadius: 1,
                    mb: 1,
                    cursor: 'crosshair',
                    backgroundColor: '#fff'
                  }}
                >
                  <canvas
                    ref={canvasRef}
                    width={field.type === 'initials' ? 200 : 400}
                    height={field.type === 'initials' ? 100 : 160}
                    onMouseDown={(e) => !disabled && startDrawing(e)}
                    onMouseMove={(e) => !disabled && draw(e)}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      if (!disabled) startDrawing(pointerToClient(e));
                    }}
                    onTouchMove={(e) => {
                      e.preventDefault();
                      if (!disabled) draw(pointerToClient(e));
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      stopDrawing();
                    }}
                    style={{
                      width: '100%',
                      height: field.type === 'initials' ? 100 : 160,
                      display: 'block',
                      touchAction: 'none'
                    }}
                  />
                </Paper>
              )}
              {values?.[field.id] && (
                <Box sx={{ mb: 1 }}>
                  <Box
                    component="img"
                    src={values[field.id]}
                    alt={label}
                    sx={{ maxWidth: '100%', maxHeight: 80, border: '1px solid #eee' }}
                  />
                </Box>
              )}
              <Button
                size="small"
                startIcon={<ClearIcon />}
                disabled={disabled}
                onClick={() => clearInk(field.id)}
              >
                Clear
              </Button>
            </Box>
          );
        }

        if (field.type === 'checkbox') {
          const checked =
            values?.[field.id] === true ||
            values?.[field.id] === 'true' ||
            values?.[field.id] === 1 ||
            values?.[field.id] === '1' ||
            values?.[field.id] === 'yes';
          return (
            <FormControlLabel
              key={field.id}
              sx={{ mb: 1.5, alignItems: 'flex-start', ml: 0 }}
              control={
                <Checkbox
                  checked={Boolean(checked)}
                  disabled={disabled}
                  onChange={(e) => setValue(field.id, e.target.checked)}
                />
              }
              label={
                <Typography variant="body2" sx={{ pt: 1 }}>
                  {label}
                  {field.required !== false ? ' *' : ''}
                </Typography>
              }
            />
          );
        }

        const defaultDate =
          field.type === 'dateSigned' ? new Date().toISOString().slice(0, 10) : '';
        return (
          <TextField
            key={field.id}
            fullWidth
            size="small"
            label={label}
            required={field.required !== false}
            type={field.type === 'dateSigned' ? 'date' : 'text'}
            InputLabelProps={field.type === 'dateSigned' ? { shrink: true } : undefined}
            value={values?.[field.id] ?? (field.type === 'dateSigned' ? defaultDate : '')}
            onChange={(e) => setValue(field.id, e.target.value)}
            disabled={disabled}
            sx={{ mb: 2 }}
          />
        );
      })}

      {mergeFields.length > 0 && (
        <Box sx={{ mt: collectable.length ? 2 : 0 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            System-filled fields
          </Typography>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            These values come from the attached property, tenant, or lease and are stamped
            automatically.
          </Alert>
          {mergeFields.map((field) => {
            const keyLabel =
              mergeKeyMeta(field.mergeKey)?.label || field.label || field.mergeKey || 'Merge';
            const preview =
              mergePreview[field.id] ??
              mergePreview[field.mergeKey] ??
              '(filled when stamped)';
            return (
              <TextField
                key={field.id}
                fullWidth
                size="small"
                label={keyLabel}
                value={preview}
                InputProps={{ readOnly: true }}
                disabled
                sx={{ mb: 1.5 }}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default SignerFieldsForm;
